<script setup>
import IconCheckmark from '@manseImages/harmony/icons/icon-checkmark.png';
import IconCancel from '@manseImages/harmony/icons/icon-cancel.png';
import IconIdea from '@manseImages/harmony/icons/icon-idea.png';
import IconFlowerBouquet from '@manseImages/harmony/icons/icon-flower-bouquet.png';
import QuoteIcon from '@manseImages/harmony/quote-left.svg';
import { OHENG_COLORS, OHENG_HANJA } from './constants';
import { formatContent, formatContentArray } from '@/utils/harmonyDataTransformer';
import { useAnalysisStore } from '@/store/modules/analysis';

const fullWidthMode = inject('fullWidthMode', false);
const halfCol = computed(() => (fullWidthMode ? undefined : 6));

const analysisStore = useAnalysisStore();

const props = defineProps({
  hData: Object,
  userName: String,
  hasPermission: Boolean,
  // 구독+내사주 커버: 추가해석을 결제 없이 열람 → 가격 대신 '보기' 표시
  subscriptionCovers: Boolean,
});

/** 종합 조언 데이터 */
const advice = computed(() => props.hData?.종합조언 || {});

/** 요약 메시지 */
const summaryText = computed(() => advice.value?.summary ?? '');

/** 장점 목록 */
const strengths = computed(() => advice.value?.strengths ?? []);

/** 주의점 목록 */
const cautions = computed(() => advice.value?.cautions ?? []);

/** 조언 목록 */
const tips = computed(() => advice.value?.tips ?? []);

/** 섹션 정의 */
const sections = computed(() => [
  { key: 'strengths', icon: IconCheckmark, title: '장점', items: strengths.value },
  { key: 'cautions', icon: IconCancel, title: '주의점', items: cautions.value },
  { key: 'tips', icon: IconIdea, title: '조언', items: tips.value },
]);

/** 함께하면 좋은 활동 데이터 */
const activities = computed(() => {
  return props.hData?.함께하면좋은활동 || [];
});

/** 오행별 색상 가져오기 */
const getOhengColor = (oheng) => OHENG_COLORS[oheng] || {};

/** persons 데이터에서 이름 → 캐릭터 이미지/오행색상 매핑 */
const personMap = computed(() => {
  const src = props.hData?.persons || [];
  const map = {};
  src.forEach((d) => {
    map[d.name] = {
      characterImg: d.characterImg,
      ohengColor: OHENG_COLORS[d.oheng] || {},
    };
  });
  return map;
});

/** 문장 단위 줄바꿈 ('. ' 기준) */
const splitSentences = (text) => {
  if (!text) return '';
  return text.replace(/\. /g, '.\n');
};

/** 별점 문자열에서 별 개수 추출 (예: "⭐⭐⭐" → 3) */
const getStarCount = (starStr) => {
  if (!starStr) return 0;
  return (starStr.match(/⭐/g) || []).length;
};

/** 관계이슈의 이슈 데이터를 그룹 배열로 정규화 */
const getIssueGroups = (issues) => {
  const raw = issues?.이슈;
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return [{ key: '관계요약', items: raw }];
  }
  return [
    { key: '관계요약', items: raw.관계요약 || [] },
    { key: '긍정신호', items: raw.긍정신호 || [] },
    { key: '주의신호', items: raw.주의신호 || [] },
  ].filter((g) => g.items.length > 0);
};

/** 1:1 궁합인지 여부 (세운/월운은 1:1일 때만 표시) */
const isTwoPerson = computed(() => (props.hData?.persons?.length || 0) === 2);

/** ===== 추가해석 열림 콘텐츠 ===== */


/** 월별 이모지 */
const MONTH_EMOJIS = {
  1: '❄️', 2: '🌸', 3: '🌱', 4: '🌸', 5: '🌿', 6: '☀️',
  7: '🌻', 8: '🍉', 9: '🍂', 10: '🍁', 11: '🍃', 12: '🎄',
};

/** 추가해석이 하나라도 열려있는지 */
const hasOpenedExtras = computed(() => analysisStore.harmonyPurchasedExtraIds.size > 0);

/** 올해 구매 완료 여부 */
const isYearlyPurchased = computed(() => analysisStore.harmonyPurchasedExtraIds.has('yearly'));

/** 구매 가능한 월별 목록 (현재 월부터 3개월) */
const availableMonthIds = computed(() => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  const ids = [];
  for (let i = 0; i < 3; i++) {
    const raw = month + i;
    const m = raw > 12 ? raw - 12 : raw;
    ids.push(`month${m}`);
  }
  return ids;
});

/** 월별 모두 구매 완료 여부 */
const isAllMonthsPurchased = computed(() => {
  const purchased = analysisStore.harmonyPurchasedExtraIds;
  return availableMonthIds.value.every((id) => purchased.has(id));
});

/** 영문 카테고리 → 한글 키 매핑 */
const CATEGORY_KEY_MAP = {
  LOVER: '연인',
  FRIEND: '친구',
  BUSINESS: '비즈니스',
  FAMILY: '가족',
};

/** API 응답에서 카테고리별 데이터를 추출 */
const extractSections = (apiContent) => {
  if (!apiContent) return null;
  // 구독자는 구매 로그(harmonyMatchedLog)가 없으므로 현재 조합 카테고리로 폴백
  const category =
    analysisStore.harmonyMatchedLog?.category || analysisStore.harmonyPaymentData?.category;
  const categoryKey = CATEGORY_KEY_MAP[category] || '연인';
  const targetName = apiContent.대상;
  return apiContent.대상별결과?.[targetName]?.[categoryKey] || null;
};

/** 열린 추가해석 항목 목록 */
const openedExtras = computed(() => {
  const ids = analysisStore.harmonyPurchasedExtraIds;
  const contents = analysisStore.harmonyExtraContents;
  const now = new Date();
  const year = now.getFullYear();

  const items = [];

  if (ids.has('yearly')) {
    const apiContent = contents['yearly'];
    const sections = extractSections(apiContent);
    items.push({
      id: 'yearly',
      emoji: '🐴',
      label: `${year}년`,
      summary: sections?.['0_요약'] ? { ...sections['0_요약'], 한줄요약: formatContent(sections['0_요약'].한줄요약) } : null,
      relationFortune: sections?.['1_관계운세'] || null,
      detail: formatContentArray(sections?.['2_상세해석'] || []),
      issues: sections?.['3_관계이슈'] || null,
    });
  }

  // 월별 항목 정렬
  const monthIds = [...ids]
    .filter((id) => id.startsWith('month'))
    .sort((a, b) => parseInt(a.replace('month', '')) - parseInt(b.replace('month', '')));

  monthIds.forEach((id) => {
    const month = parseInt(id.replace('month', ''));
    const apiContent = contents[id];
    const sections = extractSections(apiContent);
    items.push({
      id,
      emoji: MONTH_EMOJIS[month] || '📅',
      label: `${month}월`,
      summary: sections?.['0_요약'] ? { ...sections['0_요약'], 한줄요약: formatContent(sections['0_요약'].한줄요약) } : null,
      relationFortune: sections?.['1_관계운세'] || null,
      detail: formatContentArray(sections?.['2_상세해석'] || []),
      issues: sections?.['3_관계이슈'] || null,
    });
  });

  return items;
});

/** 아코디언 열림/닫힘 상태 */
const openAccordions = ref({});

/** 열린 항목이 바뀌면 새로 추가된 항목은 기본 열림 */
watch(openedExtras, (items) => {
  const next = { ...openAccordions.value };
  items.forEach((item) => {
    if (!(item.id in next)) next[item.id] = false;
  });
  openAccordions.value = next;
}, { immediate: true });

const toggleAccordion = (id) => {
  openAccordions.value = { ...openAccordions.value, [id]: !openAccordions.value[id] };
};

/** 해당 extra의 콘텐츠가 아직 로딩 중인지 판별 */
const isExtraLoading = (id) => {
  return analysisStore.harmonyPurchasedExtraIds.has(id) && !analysisStore.harmonyExtraContents[id];
};

/** 추가해석 가격 (store에서 전달받거나 기본값 사용) */
const yearlyPrice = computed(() => analysisStore.harmonyExtraYearlyPrice ?? 300);
const monthlyPrice = computed(() => analysisStore.harmonyExtraMonthlyPrice ?? 300);

/** mood card 결과보기 클릭 → 추가해석 결제 팝업 트리거 */
const onClickMoodYearly = () => {
  analysisStore.triggerHarmonyExtraPayment('yearly');
};
const onClickMoodMonthly = () => {
  analysisStore.triggerHarmonyExtraPayment('monthly-select');
};
</script>

<template>
  <VRow class="full-height">
    <!-- 왼쪽 페이지: 종합 조언 -->
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-first">
      <VCard class="advice-card full-height right">
        <div class="advice-con">
          <div class="advice-main-card">
            <!-- 제목 -->
            <p class="advice-main-card__title">종합 조언</p>

            <!-- 요약 메시지 박스 -->
            <div class="advice-summary-box">
              <QuoteIcon class="advice-summary-box__quote" />
              <p class="advice-summary-box__text" v-html="summaryText"></p>
              <QuoteIcon class="advice-summary-box__quote advice-summary-box__quote--right" />
            </div>

            <!-- 장점 / 주의점 / 조언 섹션 -->
            <template v-for="(section, sIdx) in sections" :key="section.key">
              <!-- 구분선 -->
              <div v-if="sIdx > 0" class="advice-divider" />

              <div class="advice-section">
                <img :src="section.icon" :alt="section.title" class="advice-section__icon" />
                <div class="advice-section__content">
                  <p class="advice-section__title">{{ section.title }}</p>
                  <p
                    v-for="(item, iIdx) in section.items"
                    :key="iIdx"
                    class="advice-section__item"
                    v-html="item"
                  >
                  </p>
                </div>
              </div>
            </template>
          </div>
        </div>
      </VCard>
    </VCol>

    <!-- 오른쪽 페이지: 함께하면 좋은 활동 + 분위기/추가해석 -->
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-second">
      <VCard class="advice-card full-height">
        <div class="advice-con">
          <!-- 함께하면 좋은 활동 -->
          <div class="activity-card">
            <div class="activity-card__header">
              <img :src="IconFlowerBouquet" alt="함께하면 좋은 활동" class="activity-card__header-icon" />
              <p class="activity-card__header-title">함께하면 좋은 활동</p>
            </div>

            <div
              v-for="(act, aIdx) in activities"
              :key="aIdx"
              class="activity-item"
              :style="{
                background: getOhengColor(act.oheng).bg,
              }"
            >
              <p class="activity-item__title" :style="{ fontWeight: 600 }">
                {{ act.title }}
              </p>
              <p class="activity-item__desc" v-html="act.desc"></p>
              <div class="activity-item__keywords">
                <span
                  v-for="(kw, kIdx) in act.keywords"
                  :key="kIdx"
                  class="activity-keyword"
                  :style="{
                    background: `${getOhengColor(act.oheng).text}1A`,
                    color: getOhengColor(act.oheng).text,
                  }"
                >
                  {{ kw }}
                </span>
              </div>
            </div>
          </div>

          <!-- 구독 자동로드 중(콘텐츠 도착 전): 로딩 표시 (mood 카드 대신) -->
          <div
            v-if="
              subscriptionCovers &&
              isTwoPerson &&
              analysisStore.harmonyExtrasAutoLoading &&
              !hasOpenedExtras
            "
            class="extra-loading"
          >
            <VProgressCircular indeterminate size="28" width="2" color="#9C7AEA" />
            <span class="extra-loading__text">추가 해석을 불러오는 중...</span>
          </div>

          <!-- ===== 미구매 mood-card: 올해/월별 개별 노출 (비구독자, 1:1만) ===== -->
          <template
            v-if="!subscriptionCovers && isTwoPerson && (!isYearlyPurchased || !isAllMonthsPurchased)"
          >
            <!-- 올 해, 우리 사이 분위기 (올해 미구매 시) -->
            <div v-if="!isYearlyPurchased" class="mood-card">
              <p class="mood-card__title">🌙 올 해, 우리 사이 분위기</p>
              <div class="mood-card__price-btn" @click="onClickMoodYearly">
                {{ subscriptionCovers ? '보기' : `${yearlyPrice} P` }}
              </div>
            </div>

            <!-- 월별, 우리 사이 분위기 (구매 가능한 월이 남아있을 때) -->
            <div v-if="!isAllMonthsPurchased" class="mood-card">
              <p class="mood-card__title">🎵 월별, 우리 사이 분위기</p>
              <div class="mood-card__price-btn" @click="onClickMoodMonthly">
                {{ subscriptionCovers ? '보기' : `${monthlyPrice} P` }}
              </div>
            </div>
          </template>

          <!-- ===== 추가해석 열림 시: 아코디언 컨텐츠 (1:1만) ===== -->
          <template v-if="isTwoPerson && hasOpenedExtras">
            <div
              v-for="extra in openedExtras"
              :key="extra.id"
              class="extra-accordion"
            >
              <!-- 아코디언 헤더 -->
              <div
                class="extra-accordion__header"
                @click="toggleAccordion(extra.id)"
              >
                <div class="extra-accordion__label">
                  <span class="extra-accordion__emoji">{{ extra.emoji }}</span>
                  <span class="extra-accordion__title">{{ extra.label }}</span>
                </div>
                <i
                  class="extra-accordion__arrow"
                  :class="{ open: openAccordions[extra.id] }"
                />
              </div>

              <!-- 아코디언 본문 -->
              <div
                v-if="openAccordions[extra.id]"
                class="extra-accordion__body"
              >
                <!-- 로딩 중 -->
                <div v-if="isExtraLoading(extra.id)" class="extra-loading">
                  <VProgressCircular indeterminate size="28" width="2" color="#9C7AEA" />
                  <span class="extra-loading__text">해석 데이터를 불러오는 중...</span>
                </div>
                <!-- 0_요약 -->
                <div v-else-if="extra.summary" class="extra-summary">
                  <p class="extra-summary__sub">{{ extra.summary.세운 }}{{ extra.id === 'yearly' ? '년' : '월' }}</p>
                  <div class="extra-summary__stars">
                    <i
                      v-for="i in getStarCount(extra.summary.전체별점)"
                      :key="i"
                      class="extra-summary__star"
                    />
                  </div>
                  <p class="extra-summary__grade">{{ extra.summary.전체등급 }}</p>
                  <p class="extra-summary__text" :class="{ 'no-data': !extra.summary.한줄요약 }" v-html="extra.summary.한줄요약 || '데이터 없음'"></p>
                </div>

                <!-- 1_관계운세 -->
                <template v-if="extra.relationFortune">
                  <p class="extra-section-title">🍀 관계운세</p>
                  <div
                    v-for="(fortune, name, idx) in extra.relationFortune"
                    :key="name"
                    class="extra-person"
                    :class="idx % 2 === 0 ? 'extra-person--pink' : 'extra-person--yellow'"
                  >
                    <!-- 프로필 영역 -->
                    <div class="extra-person__profile">
                      <div
                        class="extra-person__avatar"
                        :style="{
                          borderColor: personMap[name]?.ohengColor?.border || '#ccc',
                        }"
                      >
                        <img
                          v-if="personMap[name]?.characterImg"
                          :src="personMap[name].characterImg"
                          alt=""
                          class="extra-person__avatar-img"
                        />
                        <span v-else class="extra-person__avatar-emoji">{{ fortune.이모지 }}</span>
                      </div>
                      <div class="extra-person__info">
                        <p class="extra-person__name">{{ name }}</p>
                        <div class="extra-person__stars">
                          <i
                            v-for="i in getStarCount(fortune.별점)"
                            :key="i"
                            class="extra-person__star"
                          />
                        </div>
                      </div>
                    </div>
                    <!-- 상태 -->
                    <p class="extra-person__status" :class="{ 'no-data': !fortune.상태 }" v-html="formatContent(fortune.상태) || '데이터 없음'"></p>
                    <!-- 흐름 -->
                    <div class="extra-person__card">
                      <p class="extra-person__card-label">흐름</p>
                      <p class="extra-person__card-text" :class="{ 'no-data': !fortune.흐름 }" v-html="formatContent(fortune.흐름) || '데이터 없음'"></p>
                    </div>
                    <!-- 테마 -->
                    <div class="extra-person__card">
                      <p class="extra-person__card-label">테마</p>
                      <p class="extra-person__card-text" :class="{ 'no-data': !fortune.테마 }" v-html="formatContent(fortune.테마) || '데이터 없음'"></p>
                    </div>
                    <!-- 키워드 + 이모지 -->
                    <div class="extra-person__bottom">
                      <div class="extra-person__card extra-person__card--keywords">
                        <p class="extra-person__card-label">키워드</p>
                        <div class="extra-person__keywords">
                          <template v-if="fortune.키워드?.length">
                            <span
                              v-for="(kw, kIdx) in fortune.키워드"
                              :key="kIdx"
                              class="extra-person__keyword"
                            >{{ kw }}</span>
                          </template>
                          <span v-else class="extra-person__card-text no-data">데이터 없음</span>
                        </div>
                      </div>
                      <div class="extra-person__card extra-person__card--emoji">
                        <p class="extra-person__card-label">이모지</p>
                        <span class="extra-person__emoji-large">{{ fortune.이모지?.split('(')[0] || '-' }}</span>
                      </div>
                    </div>
                  </div>
                </template>

                <!-- 구분선 -->
                <div v-if="extra.detail?.length" class="extra-divider" />

                <!-- 2_상세해석 -->
                <p v-if="extra.detail?.length" class="extra-section-title">🔮 상세해석</p>
                <div v-if="extra.detail?.length" class="extra-detail">
                  <p
                    v-for="(text, dIdx) in extra.detail"
                    :key="dIdx"
                    class="extra-detail__text"
                    v-html="text"
                  ></p>
                </div>

                <!-- 구분선 -->
                <div v-if="extra.issues" class="extra-divider" />

                <!-- 3_관계이슈 -->
                <p v-if="extra.issues" class="extra-section-title">🤝 관계 이슈</p>
                <div v-if="extra.issues" class="extra-issues">
                  <!-- 그룹별 렌더링: 관계요약 / 긍정신호 / 주의신호 -->
                  <template
                    v-for="group in getIssueGroups(extra.issues)"
                    :key="group.key"
                  >
                    <!-- 관계요약: 기존 하이라이트 카드 스타일 -->
                    <template v-if="group.key === '관계요약'">
                      <div
                        v-for="(issue, iIdx) in group.items"
                        :key="`summary-${iIdx}`"
                        class="extra-issues__card"
                      >
                        <div class="extra-issues__direction-wrap">
                          <span class="extra-issues__highlight" />
                          <span class="extra-issues__direction">{{ issue.방향 }}</span>
                        </div>
                        <p class="extra-issues__content" :class="{ 'no-data': !issue.내용 }" v-html="formatContent(splitSentences(issue.내용)) || '데이터 없음'"></p>
                      </div>
                    </template>

                    <!-- 긍정신호 -->
                    <div
                      v-else-if="group.key === '긍정신호'"
                      class="extra-issues__signal extra-issues__signal--good"
                    >
                      <p class="extra-issues__signal-label">✨ 긍정신호</p>
                      <div
                        v-for="(issue, iIdx) in group.items"
                        :key="`good-${iIdx}`"
                        class="extra-issues__signal-item"
                      >
                        <p class="extra-issues__signal-content" :class="{ 'no-data': !issue.내용 }" v-html="formatContent(splitSentences(issue.내용)) || '데이터 없음'"></p>
                      </div>
                    </div>

                    <!-- 주의신호 -->
                    <div
                      v-else-if="group.key === '주의신호'"
                      class="extra-issues__signal extra-issues__signal--warn"
                    >
                      <p class="extra-issues__signal-label">⚠️ 주의신호</p>
                      <div
                        v-for="(issue, iIdx) in group.items"
                        :key="`warn-${iIdx}`"
                        class="extra-issues__signal-item"
                      >
                        <p class="extra-issues__signal-content" :class="{ 'no-data': !issue.내용 }" v-html="formatContent(splitSentences(issue.내용)) || '데이터 없음'"></p>
                      </div>
                    </div>
                  </template>
                  <!-- 포커스 -->
                  <div v-if="extra.issues.포커스" class="extra-issues__box extra-issues__box--focus">
                    <p class="extra-issues__box-label">포커스</p>
                    <p class="extra-issues__box-text" :class="{ 'no-data': !extra.issues.포커스 }" v-html="formatContent(extra.issues.포커스) || '데이터 없음'"></p>
                  </div>
                  <!-- 좋은점 -->
                  <div v-if="extra.issues.좋은점" class="extra-issues__box extra-issues__box--good">
                    <p class="extra-issues__box-label">좋은점</p>
                    <p class="extra-issues__box-text" :class="{ 'no-data': !extra.issues.좋은점 }" v-html="formatContent(extra.issues.좋은점) || '데이터 없음'"></p>
                  </div>
                  <!-- 주의점 -->
                  <div v-if="extra.issues.주의점" class="extra-issues__box extra-issues__box--warn">
                    <p class="extra-issues__box-label">주의점</p>
                    <p class="extra-issues__box-text" :class="{ 'no-data': !extra.issues.주의점 }" v-html="formatContent(extra.issues.주의점) || '데이터 없음'"></p>
                  </div>
                </div>
              </div>
            </div>
          </template>

        </div>
      </VCard>
    </VCol>
  </VRow>
</template>

<style lang="scss" scoped>
@import '@/assets/nangman_style/_analysis_main_color.scss';

.no-data {
  color: #e53935 !important;
  font-style: italic;
}

.advice-card {
  border-radius: 0 !important;
  box-shadow: none !important;
  padding: 0;
  overflow-y: auto;

  &.right {
    border-right: 1px solid var(--Gray-300, #ebebeb);
  }
}

.advice-con {
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: center;
  justify-content: center;
  padding: 24px 18px;
  width: 100%;
  box-sizing: border-box;
}

/* 메인 카드 */
.advice-main-card {
  display: flex;
  flex-direction: column;
  gap: 15px;
  align-items: center;
  padding: 20px 16px;
  border-radius: 12px;
  background: var(--Orange-100, #fff8f5);
  width: 100%;
  box-sizing: border-box;

  &__title {
    color: var(--Gray-800, #333);
    font-size: 18px;
    font-weight: 700;
    line-height: 1.5;
    text-align: center;
    width: 100%;
  }
}

/* 요약 메시지 박스 */
.advice-summary-box {
  display: flex;
  align-items: center;
  gap: 30px;
  padding: 20px 16px;
  border-radius: 12px;
  background: var(--gray-100-white, #fff);
  width: 100%;
  justify-content: center;
  box-sizing: border-box;

  &__quote {
    width: 15px;
    height: 14px;
    flex-shrink: 0;

    &--right {
      transform: rotate(180deg);
    }
  }

  &__text {
    color: var(--Gray-800, #333);
    font-size: 15px;
    font-weight: 500;
    line-height: 24px;
    text-align: center;
    white-space: pre-line;
    min-width: 0;
  }
}

/* 구분선 */
.advice-divider {
  width: 100%;
  height: 1px;
  background: var(--Gray-300, #ebebeb);
}

/* 섹션 (장점/주의점/조언) */
.advice-section {
  display: flex;
  gap: 20px;
  align-items: center;
  padding: 0 30px;
  width: 100%;
  box-sizing: border-box;

  &__icon {
    width: 57px;
    height: 57px;
    flex-shrink: 0;
    object-fit: contain;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 0;
    flex: 1;
  }

  &__title {
    color: var(--Gray-800, #333);
    font-size: 15px;
    font-weight: 700;
    line-height: 24px;
  }

  &__item {
    color: var(--Gray-700, #575757);
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
    margin-top: 5px;
  }
}

/* ── 오른쪽 페이지: 함께하면 좋은 활동 ── */
.activity-card {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid var(--Gray-300, #ebebeb);
  background: var(--gray-100-white, #fff);
  width: 100%;
  box-sizing: border-box;

  &__header {
    display: flex;
    gap: 5px;
    align-items: center;
  }

  &__header-icon {
    width: 24px;
    height: 24px;
    object-fit: contain;
  }

  &__header-title {
    color: var(--Gray-800, #333);
    font-size: 15px;
    font-weight: 700;
    line-height: 24px;
  }
}

/* 활동 아이템 */
.activity-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border-radius: 12px;
  width: 100%;
  box-sizing: border-box;

  &__title {
    color: var(--Gray-800, #333);
    font-size: 15px;
    font-weight: 600;
    line-height: 24px;
  }

  &__desc {
    color: var(--Gray-700, #575757);
    font-size: 15px;
    font-weight: 500;
    line-height: 24px;
  }

  &__keywords {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
  }
}

/* 활동 키워드 태그 */
.activity-keyword {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3px 6px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 700;
  line-height: 18px;
  white-space: nowrap;
}

/* ── 오른쪽 페이지: 분위기 카드 ── */
.mood-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid var(--Gray-300, #ebebeb);
  background: var(--gray-100-white, #fff);
  width: 100%;
  box-sizing: border-box;

  &__title {
    color: var(--Gray-800, #333);
    font-size: 15px;
    font-weight: 700;
    line-height: 24px;
    white-space: nowrap;
  }

  &__price-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6px 12px;
    border-radius: 100px;
    border: 1px solid var(--Gray-300, #ebebeb);
    background: var(--gray-100-white, #fff);
    color: var(--Gray-600, #757575);
    font-size: 15px;
    font-weight: 500;
    line-height: 20px;
    white-space: nowrap;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      border-color: var(--orange-300-main, #eb4c10);
      color: var(--orange-300-main, #eb4c10);
      background: var(--Orange-100, #fff8f5);
    }
  }
}

/* ── 추가해석 아코디언 ── */
.extra-accordion {
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  border: 1px solid var(--Gray-300, #ebebeb);
  background: var(--gray-100-white, #fff);
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px;
    cursor: pointer;
    user-select: none;
    transition: background 0.15s;

    &:hover {
      background: var(--Gray-200, #f8f8f8);
    }
  }

  &__label {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__emoji {
    font-size: 18px;
    line-height: 1;
  }

  &__title {
    color: var(--Gray-800, #333);
    font-size: 15px;
    font-weight: 700;
    line-height: 24px;
  }

  &__arrow {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    background: url('@manseImages/icon_info_detail.png') center center no-repeat;
    background-size: 20px auto;
    transform: rotate(90deg);
    transition: transform 0.2s ease;

    &.open {
      transform: rotate(-90deg);
    }
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 0 16px 20px;
  }
}

/* ── 0_요약 ── */
.extra-summary {
  display: flex;
  flex-direction: column;
  gap: 15px;
  align-items: center;
  padding: 20px 16px;
  border-radius: 12px;

  &__sub {
    color: var(--Gray-600, #757575);
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
    text-align: center;
  }

  &__stars {
    display: flex;
    align-items: center;
    gap: 2px;
  }

  &__star {
    display: flex;
    width: 24px;
    height: 24px;
    mask: url('/src/assets/images_manse/icon_bookmark.svg') no-repeat center;
    mask-size: contain;
    background-color: #fbdb68;
  }

  &__grade {
    color: var(--Gray-800, #333);
    font-size: 25px;
    font-weight: 700;
    line-height: 1.3;
    letter-spacing: 2px;
    text-align: center;
  }

  &__text {
    color: var(--Gray-700, #575757);
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
    text-align: center;
    white-space: pre-line;
  }
}

/* ── 공통 섹션 제목 ── */
.extra-section-title {
  color: var(--Gray-800, #333);
  font-size: 15px;
  font-weight: 700;
  line-height: 24px;
}

/* ── 구분선 ── */
.extra-divider {
  width: 100%;
  height: 1px;
  background: var(--Gray-300, #ebebeb);
}

/* ── 1_관계운세 ── */
.extra-person {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  border-radius: 12px;
  width: 100%;
  box-sizing: border-box;

  &--pink {
    background: rgba(194, 53, 53, 0.05);
  }

  &--yellow {
    background: #fdfaf4;
  }

  &__profile {
    display: flex;
    align-items: center;
    gap: 12px;
    justify-content: center;
  }

  &__avatar {
    width: 74px;
    height: 74px;
    border-radius: 50%;
    border: 3px solid #ccc;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &__avatar-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__avatar-emoji {
    font-size: 36px;
    line-height: 1;
  }

  &__info {
    display: flex;
    flex-direction: column;
    gap: 5px;
    align-items: flex-start;
  }

  &__name {
    color: var(--Gray-800, #333);
    font-size: 15px;
    font-weight: 700;
    line-height: 1.3;
    padding: 0 5px;
  }

  &__stars {
    display: flex;
    align-items: center;
    gap: 2px;
  }

  &__star {
    display: flex;
    width: 18px;
    height: 18px;
    mask: url('/src/assets/images_manse/icon_bookmark.svg') no-repeat center;
    mask-size: contain;
    background-color: #fbdb68;
  }

  &__rate {
    color: var(--Gray-700, #575757);
    font-size: 15px;
    font-weight: 400;
    line-height: 1.3;
    padding: 0 5px;
  }

  &__status {
    color: var(--Gray-800, #333);
    font-size: 15px;
    font-weight: 600;
    line-height: 24px;
    text-align: center;
    width: 100%;
  }

  &__card {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.8);
    width: 100%;
    box-sizing: border-box;
  }

  &__card-label {
    color: var(--Gray-800, #333);
    font-size: 15px;
    font-weight: 600;
    line-height: 24px;
  }

  &__card-text {
    color: var(--Gray-800, #333);
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
  }

  &__bottom {
    display: flex;
    gap: 8px;
    width: 100%;
  }

  &__card--keywords {
    flex: 1;
    min-width: 0;
  }

  &__card--emoji {
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    padding: 12px 24px;
    width: auto;
  }

  &__keywords {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__keyword {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 3px 6px;
    border-radius: 4px;
    background: rgba(255, 107, 107, 0.1);
    color: #ff6b6b;
    font-size: 13px;
    font-weight: 600;
    line-height: 18px;
    white-space: nowrap;
  }

  &__emoji-large {
    font-size: 24px;
    line-height: 1;
  }
}

/* ── 2_상세해석 ── */
.extra-detail {
  display: flex;
  flex-direction: column;
  gap: 5px;

  &__text {
    color: var(--Gray-800, #333);
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
    white-space: pre-line;
  }
}

/* ── 3_관계이슈 ── */
.extra-issues {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__card {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 0 16px;
    border-radius: 12px;
    background: #fff;
  }

  &__direction-wrap {
    position: relative;
    display: inline-flex;
    align-self: center;
  }

  &__highlight {
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: calc(100% + 16px);
    height: 12px;
    background: rgba(255, 107, 107, 0.15);
  }

  &__direction {
    position: relative;
    color: var(--Gray-800, #333);
    font-size: 20px;
    font-weight: 700;
    line-height: 1.3;
    text-align: center;
  }

  &__content {
    color: var(--Gray-700, #575757);
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
    text-align: center;
    white-space: pre-line;
  }

  &__box {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 12px 16px;
    border-radius: 8px;
    width: 100%;
    box-sizing: border-box;

    &--focus {
      background: #f2f8ff;
    }

    &--good {
      background: #f6fbf7;
    }

    &--warn {
      background: #fff5ef;
      opacity: 0.9;
    }
  }

  &__box-label {
    color: var(--Gray-800, #333);
    font-size: 15px;
    font-weight: 700;
    line-height: 24px;
  }

  &__box-text {
    color: var(--Gray-700, #575757);
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
  }

  /* 긍정/주의 신호 박스 */
  &__signal {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 12px 16px;
    border-radius: 8px;
    width: 100%;
    box-sizing: border-box;

    &--good {
      background: #f6fbf7;
      border: 1px solid rgba(76, 175, 80, 0.2);
    }

    &--warn {
      background: #fff5ef;
      border: 1px solid rgba(255, 152, 0, 0.2);
    }
  }

  &__signal-label {
    color: var(--Gray-800, #333);
    font-size: 15px;
    font-weight: 700;
    line-height: 24px;
  }

  &__signal-item {
    display: flex;
    flex-direction: column;
    gap: 4px;

    & + & {
      margin-top: 6px;
      padding-top: 10px;
      border-top: 1px dashed rgba(0, 0, 0, 0.06);
    }
  }

  &__signal-content {
    color: var(--Gray-700, #575757);
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
    white-space: pre-line;
  }
}

.extra-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 32px 16px;

  &__text {
    color: var(--Gray-500, #999);
    font-size: 14px;
    font-weight: 400;
  }
}
</style>
